import {
    normalizeSchoolDocumentAudienceMode,
    normalizeSchoolDocumentNotificationMode,
} from './schoolDocumentModes';

/**
 * Normalised school document list item (POST /school/documents) including audience fields.
 * @typedef {object} SchoolDocumentListItem
 * @property {number|string} id
 * @property {string} name
 * @property {boolean} strictRecipientsFiltering
 * @property {boolean} isWholeSchoolAudience
 * @property {number} [audienceMode] 0 Static, 1 Dynamic
 * @property {number} [notificationMode] 0 None, 1 InitialOnly, 2 InitialAndNewEligible
 * @property {{ filterId: number, excluded: boolean, filterName: string|null }[]} [groupFilters]
 */

/**
 * @param {SchoolDocumentListItem} doc
 */
export function formatSchoolDocumentAudienceSummary(doc) {
    const groups = doc.groupFilters || [];
    if (doc.isWholeSchoolAudience || groups.length === 0) {
        return 'All student profiles';
    }
    return groups
        .map((g) =>
            g.filterName != null && String(g.filterName).trim() !== ''
                ? g.filterName
                : `Filter ${g.filterId}`)
        .join(', ');
}

/**
 * Maps API list item + editor shape for {@link DocumentAudienceGroupFields}.
 * @param {SchoolDocumentListItem} doc
 */
export function schoolDocumentToAudienceEditorState(doc) {
    const groupFilters = (doc.groupFilters || []).map((g) => ({
        id: g.filterId,
        displayName:
            g.filterName != null && String(g.filterName).trim() !== ''
                ? g.filterName
                : `Filter ${g.filterId}`,
        excluded: !!g.excluded,
    }));
    return {
        strictRecipientsFiltering: !!doc.strictRecipientsFiltering && groupFilters.length > 1,
        audienceMode: normalizeSchoolDocumentAudienceMode(doc.audienceMode),
        notificationMode: normalizeSchoolDocumentNotificationMode(doc.notificationMode),
        audienceSettings: {
            groupFilters,
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: [],
            employeeFilters: [],
        },
    };
}

/**
 * @param {{ id: number, excluded: boolean }[]} groupFilters
 * @param {boolean} strictRecipientsFiltering
 * @param {{ audienceMode?: number, notificationMode?: number }} [modes]
 */
export function buildUpdateSchoolDocumentAudienceBody(groupFilters, strictRecipientsFiltering, modes = {}) {
    const body = {
        groupFilters: groupFilters.map((f) => ({
            filterId: f.id,
            excluded: !!f.excluded,
        })),
        strictRecipientsFiltering:
            groupFilters.length > 1 ? !!strictRecipientsFiltering : false,
    };
    if (modes.audienceMode !== undefined) {
        body.audienceMode = modes.audienceMode;
    }
    if (modes.notificationMode !== undefined) {
        body.notificationMode = modes.notificationMode;
    }
    return body;
}
